import { generateUUIDv4 } from '@bitjourney/uuid-v4';
import { currentLocaleTag, t } from 'src/i18n';
import { LocalDate } from 'src/utilities/local-date';

// pdfjs-dist lastes ved første bruk: text.ts importeres av store deler av appen
// for småfunksjoner (uuid m.m.), og en statisk import her ville dratt hele
// pdfjs inn i entry-bundelen (#13442).
async function loadPdfjs() {
  const pdfjsLib = await import('pdfjs-dist');
  // Use new URL() for proper worker path resolution in Chrome and Safari
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).href;
  return pdfjsLib;
}

export function ordinal(n: number) {
  const ordinals = [
    'første',
    'andre',
    'tredje',
    'fjerde',
    'femte',
    'sjette',
    'syvende',
    'åttende',
    'niende',
    'tiende',
    'ellevte',
    'tolvte',
  ];
  if (n < 13) {
    return ordinals[n - 1];
  }
  return n + '.';
}

export function uuid(): string {
  return generateUUIDv4();
}

/**
 * Joins the members of the array seperated by comman and the last elements seperated by ' og ' (and).
 * @param list
 */
export function joinWithAnd(list: string[]): string {
  let text = '';
  list.forEach(function (item, index) {
    let divider = '';
    if (index < list.length - 1 && list.length > 2) {
      divider = ', ';
    }
    if (index === list.length - 2) {
      divider = t('text.listAnd');
    }
    text += item + divider;
  });
  return text;
}

export function namesListFromUuids(list: { value: string; text: string }[], uuids: string[]) {
  return joinWithAnd(list.filter((l) => uuids.includes(l.value)).map((l) => l.text));
}

export function numberWord(n: number, neuter: boolean = false): string {
  if (n === 0) {
    return 'ingen';
  }
  if (n === 1) {
    if (neuter) {
      return 'ett';
    }
    return 'én';
  }
  if (n === 2) {
    return 'to';
  }
  if (n === 3) {
    return 'tre';
  }
  if (n === 4) {
    return 'fire';
  }
  if (n === 5) {
    return 'fem';
  }
  if (n === 6) {
    return 'seks';
  }
  if (n === 7) {
    return 'syv';
  }
  if (n === 8) {
    return 'åtte';
  }
  if (n === 9) {
    return 'ni';
  }
  if (n === 10) {
    return 'ti';
  }
  if (n === 11) {
    return 'elleve';
  }
  if (n === 12) {
    return 'tolv';
  }
  return n.toString();
}

export function relativeDaysAsText(day: string | undefined, today: LocalDate) {
  if (!day || !today) {
    return '';
  }
  const days = today.until(LocalDate.fromString(day));
  if (days < -365 || days > 365) {
    return '';
  }
  if (days < -1) {
    return t('dates.daysAgo', { count: numberWord(days * -1) });
  }
  if (days === -1) {
    return t('dates.yesterday');
  }
  if (days === 0) {
    return t('dates.today');
  }
  if (days === 1) {
    return t('dates.tomorrow');
  }
  if (days > 1) {
    return t('dates.inDays', { count: numberWord(days) });
  }
}

export function relativePastTimeAsText(dateTime: string | undefined): string {
  if (!dateTime) {
    return '';
  }
  const then = new Date(dateTime).getTime();
  if (Number.isNaN(then)) {
    return '';
  }
  const rtf = new Intl.RelativeTimeFormat(currentLocaleTag(), { numeric: 'auto' });
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) {
    return rtf.format(0, 'second');
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return rtf.format(-minutes, 'minute');
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return rtf.format(-hours, 'hour');
  }
  const days = Math.floor(hours / 24);
  if (days < 31) {
    return rtf.format(-days, 'day');
  }
  const months = Math.floor(days / 30);
  return rtf.format(-months, 'month');
}

export function capitalize(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function zeropad(n) {
  return ('00' + n).slice(-2);
}

export function minutesToTime(n) {
  const toFive = Math.floor(n / 5) * 5;
  const hours = toFive / 60;
  const rhours = Math.floor(hours);
  const minutes = (hours - rhours) * 60;
  const rminutes = Math.round(minutes);
  return zeropad(rhours) + ':' + zeropad(rminutes);
}

export function isEmptyOrInvalidString(string) {
  if (string === undefined || string === null) {
    return true;
  }
  return string.replace('&nbsp;', '').trim() === '';
}

export function isEmptyHtmlContent(s: string | undefined): boolean {
  return s === undefined || s === '' || s === '<p></p>';
}

/**
 * Returns the plain-text content of a (possibly rich-text) string, with all
 * HTML markup removed, entities decoded and surrounding whitespace trimmed.
 * Empty markup such as `<p></p>` or `<p><br></p>` collapses to an empty string,
 * which callers can use to fall back to a default name instead of rendering
 * literal tags.
 *
 * Uses an inert `<template>` node so all entities (e.g. `&amp;` → `&`) decode
 * correctly and any markup is parsed without loading resources or running
 * scripts. Only ever called from Lit components, so a DOM is always present.
 */
export function plainTextFromHtml(s: string | undefined | null): string {
  if (s === undefined || s === null) {
    return '';
  }
  const template = document.createElement('template');
  template.innerHTML = s;
  // NBSP (U+00A0) is trimmed by String.trim, but normalise interior ones to
  // regular spaces so multi-word names read naturally.
  return (template.content.textContent ?? '').replace(/\u00a0/g, ' ').trim();
}

/**
 * Cleans a display name that *may or may not* be legacy rich-text markup.
 *
 * New slides already store a plain-text name, but names saved before markup was
 * stripped can still hold raw HTML (e.g. `<p></p>` or `<p>Hei</p>`). We only run
 * the strip when the value actually looks like markup — i.e. it contains a
 * closing tag — so applying this at render time is idempotent: legitimate
 * plain-text titles with stray angle brackets (e.g. "List<T>") are preserved
 * instead of being re-parsed as tags and silently truncated.
 *
 * Accepted residual risk: plain text that genuinely contains a `</word`
 * sequence (e.g. a title "How to close a </div> tag") will still be treated as
 * markup and over-stripped. This is vanishingly unlikely in real slide names,
 * so we accept it rather than parse more precisely.
 */
export function plainTextFromMaybeHtml(s: string | undefined | null): string {
  if (s === undefined || s === null) {
    return '';
  }
  return /<\/[a-z]/i.test(s) ? plainTextFromHtml(s) : s.trim();
}

function prepHtml(string: string): string {
  return string
    .replace(/ {2}|\r\n|\n|\r/gm, '')
    .replaceAll('> <', '><')
    .replaceAll('<li><p>', '<li>')
    .replaceAll('</p></li>', '</li>')
    .replaceAll('<br />', '<br>')
    .replaceAll('<br/>', '<br>')
    .replaceAll('<br> ', '<br>');
}

export function isSameHtmlContent(a: string, b: string): boolean {
  return prepHtml(a) === prepHtml(b);
}

export function displayName(s: string | undefined | null, defaultValue?: string): string {
  const s1 = s ?? '';
  return s1 === '' ? (defaultValue ?? t('text.withoutNameLowercase')) : s1;
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const formattedHours = hours < 10 ? `0${hours}` : `${hours}`;
  const formattedMinutes = remainingMinutes < 10 ? `0${remainingMinutes}` : `${remainingMinutes}`;

  return `${formattedHours}:${formattedMinutes}`;
}

export function convertToMinutes(timeString: string): number {
  const [hoursString, minutesString] = timeString.split(':');
  const hours = parseInt(hoursString, 10);
  const minutes = parseInt(minutesString, 10);

  if (isNaN(hours) || isNaN(minutes)) {
    throw new Error('Invalid time format. Please provide time in "hh:mm" format.');
  }

  return hours * 60 + minutes;
}

export function toDateTimeDuration(dateTime1: string, durationMinutes: number): string {
  const dateTime = dateTime1;
  const hasTime = dateTime.split(' ').length === 2;
  return dateTime + (hasTime ? ` ${durationMinutes}` : '');
}

export function levenshteinDistance(str1: string, str2: string): number {
  str1 = str1.toLowerCase();
  str2 = str2.toLowerCase();
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix = Array(len1 + 1);
  for (let i = 0; i <= len1; i++) {
    matrix[i] = Array(len2 + 1);
  }
  for (let i = 0; i <= len1; i++) {
    matrix[i][0] = i;
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(matrix[i - 1][j] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j - 1] + 1);
      }
    }
  }
  return matrix[len1][len2];
}

export function entityNameForLists(
  name: string | undefined,
  draftName: string | undefined,
  isConfirmedEntity: boolean,
): string {
  let result: string;
  if (!isConfirmedEntity) {
    result = t('text.draftPrefix');
    if (name) {
      result += name + ')';
    } else if (draftName) {
      result += draftName + ')';
    } else {
      result += t('text.withoutNameDraftSuffix');
    }
  } else {
    result = name || t('text.withoutNameUppercase');
  }
  return result;
}

export function shorten(text: string, length: number) {
  if (text.length < length) {
    return text;
  }
  return text.substring(0, length - 2) + '…';
}

async function fetchPDF(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch PDF: ${response.statusText}`);
  }
  return await response.arrayBuffer();
}

async function extractTextFromPDF(pdfData: ArrayBuffer): Promise<string> {
  const pdfjsLib = await loadPdfjs();
  const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
  const maxPages = pdf.numPages;
  const pageTextPromises: Promise<string>[] = [];

  for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();
    const pageText = textContent.items.map((item) => (item as any).str).join(' ');
    pageTextPromises.push(Promise.resolve(pageText));
  }

  const pageTexts = await Promise.all(pageTextPromises);
  return pageTexts.join('\n');
}

export async function fetchAndExtractText(pdfUrl: string): Promise<string> {
  try {
    const pdfData = await fetchPDF(pdfUrl);
    return await extractTextFromPDF(pdfData);
  } catch (error) {
    console.error('Error:', error);
    return '';
  }
}

export function memoizeAsync<F extends (...args: any[]) => Promise<any>>(fn: F): F {
  const cache = new Map<string, Promise<ReturnType<F>>>();

  return (async (...args: Parameters<F>): Promise<ReturnType<F>> => {
    const key = JSON.stringify(args);
    if (!cache.has(key)) {
      cache.set(key, fn(...args));
    }
    return cache.get(key)!;
  }) as F;
}

export const memoizedFetchAndExtract = memoizeAsync(fetchAndExtractText);

export function abbreviateDescription(description: string, maxLength: number = 40): string {
  if (description.length <= maxLength) {
    return description;
  }

  // Try to find the last space within maxLength
  const truncated = description.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');

  if (lastSpace > 0) {
    return truncated.slice(0, lastSpace) + '...';
  }

  // If no space found, just cut off at maxLength
  return truncated + '...';
}

export function fullName(firstName: string | undefined, lastName: string | undefined): string {
  if (firstName) {
    if (lastName) {
      return firstName + ' ' + lastName;
    }
    return firstName;
  }
  return lastName ?? '';
}
